import { useEffect } from "react"
import { atom, useAtomValue, useSetAtom } from "jotai"

export interface BrowserCapabilities {
	hasFileSystemAccess: boolean
	hasOPFS: boolean
	supportsSampleOnly: boolean
}

const defaultCapabilities: BrowserCapabilities = {
	hasFileSystemAccess: false,
	hasOPFS: false,
	supportsSampleOnly: false,
}

export const browserCapabilitiesAtom = atom<BrowserCapabilities>(defaultCapabilities)

interface FileSystemDirectoryHandlePrototype {
	requestPermission?: () => unknown
	removeEntry?: () => unknown
}

interface FileSystemFileHandlePrototype {
	createWritable?: () => unknown
}

interface CapabilityWindow {
	showDirectoryPicker?: unknown
	FileSystemDirectoryHandle?: {
		prototype?: FileSystemDirectoryHandlePrototype
	}
	FileSystemFileHandle?: {
		prototype?: FileSystemFileHandlePrototype
	}
}

interface CapabilityNavigator {
	storage?: {
		getDirectory?: () => unknown
	}
}

export interface CapabilityEnv {
	window?: CapabilityWindow
	navigator?: CapabilityNavigator
}

function hasFunctionalFileSystemAccess(targetWindow: CapabilityWindow): boolean {
	if (typeof targetWindow.showDirectoryPicker !== "function") {
		return false
	}

	const directoryPrototype = targetWindow.FileSystemDirectoryHandle?.prototype
	const filePrototype = targetWindow.FileSystemFileHandle?.prototype

	const hasDirectoryPermissions =
		typeof directoryPrototype?.requestPermission === "function" &&
		typeof directoryPrototype?.removeEntry === "function"
	const hasWritableFileSupport = typeof filePrototype?.createWritable === "function"

	return hasDirectoryPermissions && hasWritableFileSupport
}

export function detectBrowserCapabilities(env: CapabilityEnv = {}): BrowserCapabilities {
	const targetWindow =
		env.window ??
		(typeof window !== "undefined" ? (window as unknown as CapabilityWindow) : undefined)
	const targetNavigator =
		env.navigator ??
		(typeof navigator !== "undefined"
			? (navigator as unknown as CapabilityNavigator)
			: undefined)

	if (!targetWindow || !targetNavigator) {
		return defaultCapabilities
	}

	const hasFileSystemAccess = hasFunctionalFileSystemAccess(targetWindow)
	const hasOPFS = typeof targetNavigator.storage?.getDirectory === "function"
	const supportsSampleOnly = !hasFileSystemAccess && hasOPFS

	return {
		hasFileSystemAccess,
		hasOPFS,
		supportsSampleOnly,
	}
}

export const useBrowserCapabilities = () => useAtomValue(browserCapabilitiesAtom)

export const useDetectBrowserCapabilities = (env?: CapabilityEnv) => {
	const setCapabilities = useSetAtom(browserCapabilitiesAtom)

	useEffect(() => {
		setCapabilities(detectBrowserCapabilities(env))
	}, [env, setCapabilities])
}

/**
 * Masks are not currently supported on sample project limited browsers
 */
export function areMasksSupported(capabilities: BrowserCapabilities): boolean {
	if (capabilities.supportsSampleOnly) {
		return false
	}
	return true
}

export const areMasksSupportedAtom = atom((get) => {
	const capabilities = get(browserCapabilitiesAtom)
	return areMasksSupported(capabilities)
})

export const useAreMasksSupported = () => useAtomValue(areMasksSupportedAtom)

/**
 * Healing is not currently supported on sample project limited browsers
 */
export function isHealingSupported(capabilities: BrowserCapabilities): boolean {
	if (capabilities.supportsSampleOnly) {
		return false
	}
	return true
}

export const isHealingSupportedAtom = atom((get) => {
	const capabilities = get(browserCapabilitiesAtom)
	return isHealingSupported(capabilities)
})

export const useIsHealingSupported = () => useAtomValue(isHealingSupportedAtom)
